import { readonly, ref, shallowRef } from 'vue'

import type { ClickerState, ClickerStatus } from './types'

interface ClickerOptions {
  role: 'screen' | 'controller'
  token: string
  slug?: string
  wsPath?: string
  initial: Omit<ClickerState, 'type'>
}

type ServerMessage =
  | (ClickerState & { controllerConnected?: boolean })
  | { type: 'ack'; commandId: string; seq: number; duplicate?: boolean }
  | { type: 'error'; message?: string; code?: string; commandId?: string }

interface PendingCommand {
  commandId: string
  to: number
  baseSeq: number
}

function socketUrl(path: string, query: URLSearchParams): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const url = new URL(path, `${protocol}//${window.location.host}`)
  query.forEach((value, key) => url.searchParams.set(key, value))
  return url.toString()
}

function commandId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

export function useClicker(options: ClickerOptions) {
  const status = ref<ClickerStatus>('connecting')
  const state = ref<ClickerState>({ type: 'state', ...options.initial })
  const controllerConnected = ref(false)
  const error = ref('')
  const terminalError = ref('')
  const socket = shallowRef<WebSocket | null>(null)
  let stopped = false
  let reconnectAttempt = 0
  let reconnectTimer = 0
  let inFlight: PendingCommand | null = null
  let desiredTo: number | null = null
  let hydrated = false
  let serverSlideIndex = options.initial.slideIndex

  function sendDesired(): void {
    const connection = socket.value
    if (
      !connection
      || connection.readyState !== WebSocket.OPEN
      || !hydrated
      || inFlight
      || desiredTo === null
    ) return
    if (desiredTo === serverSlideIndex) {
      desiredTo = null
      state.value = { ...state.value, slideIndex: serverSlideIndex }
      return
    }

    inFlight = {
      commandId: commandId(),
      to: desiredTo,
      baseSeq: state.value.seq,
    }
    connection.send(JSON.stringify({
      type: 'navigate',
      commandId: inFlight.commandId,
      to: inFlight.to,
      revision: state.value.revision,
      baseSeq: inFlight.baseSeq,
    }))
  }

  function scheduleReconnect(): void {
    if (stopped) return
    status.value = reconnectAttempt === 0 ? 'connecting' : 'reconnecting'
    const delay = Math.min(10_000, 500 * (2 ** reconnectAttempt))
    const jitter = Math.floor(Math.random() * Math.min(500, delay / 3))
    reconnectAttempt = Math.min(reconnectAttempt + 1, 8)
    window.clearTimeout(reconnectTimer)
    reconnectTimer = window.setTimeout(connect, delay + jitter)
  }

  function connect(): void {
    if (stopped) return
    const current = socket.value
    if (
      current
      && (current.readyState === WebSocket.OPEN || current.readyState === WebSocket.CONNECTING)
    ) return
    hydrated = false
    inFlight = null
    status.value = reconnectAttempt > 0 ? 'reconnecting' : 'connecting'

    const query = new URLSearchParams({
      role: options.role,
      token: options.token,
    })
    if (options.role === 'screen' && options.slug) query.set('slug', options.slug)
    const connection = new WebSocket(socketUrl(options.wsPath ?? '/api/clicker/ws', query))
    socket.value = connection

    connection.addEventListener('open', () => {
      if (socket.value !== connection) return
      status.value = 'connected'
      error.value = ''
      reconnectAttempt = 0
    })

    connection.addEventListener('message', (event) => {
      if (socket.value !== connection || typeof event.data !== 'string') return
      let message: ServerMessage
      try {
        message = JSON.parse(event.data) as ServerMessage
      } catch {
        return
      }

      if (message.type === 'state') {
        if (
          hydrated
          && message.revision === state.value.revision
          && message.seq < state.value.seq
        ) return
        const wanted = desiredTo
        serverSlideIndex = message.slideIndex
        state.value = {
          type: 'state',
          revision: message.revision,
          slideIndex: wanted ?? message.slideIndex,
          seq: message.seq,
          ...(message.controllerId !== undefined
            ? { controllerId: message.controllerId }
            : {}),
        }
        if (message.controllerConnected !== undefined) {
          controllerConnected.value = message.controllerConnected
        }
        hydrated = true
        inFlight = null
        if (wanted === message.slideIndex) {
          desiredTo = null
          state.value.slideIndex = message.slideIndex
        }
        sendDesired()
        return
      }

      if (message.type === 'ack') {
        if (message.seq < state.value.seq) return
        // После обычного ack сервер тем же соединением всегда присылает полный
        // state. Ждём его и только потом отправляем следующее желаемое состояние,
        // чтобы не создать две команды на одном baseSeq.
        return
      }

      if (message.type === 'error') {
        error.value = message.message ?? 'Пульт не принял команду.'
        inFlight = null
        if (
          message.code === 'STALE_REVISION'
          || message.code === 'STALE_STATE'
          || message.code === 'FUTURE_STATE'
          || message.code === 'TARGET_OUT_OF_RANGE'
        ) {
          // Следом сервер присылает полный authoritative state. Он может иметь
          // меньший seq, если Redis-комната была пересоздана.
          hydrated = false
        }
        if (message.code === 'TARGET_OUT_OF_RANGE') {
          // После републикации колода могла стать короче, пока телефон спал.
          // Старое желаемое состояние больше нельзя повторять после full state.
          desiredTo = null
          state.value = { ...state.value, slideIndex: serverSlideIndex }
        }
        if (
          message.code === 'CONTROLLER_REPLACED'
          || message.code === 'ROOM_EXPIRED'
        ) {
          terminalError.value = error.value
          stopped = true
          status.value = 'offline'
          connection.close(1000, 'clicker session ended')
        }
      }
    })

    connection.addEventListener('close', () => {
      if (socket.value !== connection) return
      socket.value = null
      hydrated = false
      inFlight = null
      if (!stopped) scheduleReconnect()
    })

    connection.addEventListener('error', () => {
      if (socket.value !== connection) return
      error.value = 'Связь с пультом прервалась. Подключаемся снова…'
      connection.close()
    })
  }

  function navigate(to: number): void {
    const target = Math.max(0, Math.floor(to))
    desiredTo = target
    state.value = { ...state.value, slideIndex: target }
    sendDesired()
  }

  function close(): void {
    stopped = true
    window.clearTimeout(reconnectTimer)
    status.value = 'closed'
    window.removeEventListener('online', onOnline)
    document.removeEventListener('visibilitychange', onVisibility)
    socket.value?.close(1000, 'page closed')
    socket.value = null
  }

  function reconnectNow(): void {
    if (stopped) return
    window.clearTimeout(reconnectTimer)
    socket.value?.close()
    socket.value = null
    connect()
  }

  function onOnline(): void {
    reconnectNow()
  }

  function onVisibility(): void {
    if (document.visibilityState === 'visible' && status.value !== 'connected') reconnectNow()
  }

  window.addEventListener('online', onOnline)
  document.addEventListener('visibilitychange', onVisibility)

  connect()

  return {
    status: readonly(status),
    state: readonly(state),
    controllerConnected: readonly(controllerConnected),
    error: readonly(error),
    terminalError: readonly(terminalError),
    navigate,
    reconnect: reconnectNow,
    close,
  }
}
